/**
 * Shared helper for persisting accepted AI suggestions from AISuggestionsBanner.
 *
 * Each parent page supplies:
 *   - items:        the accepted suggestion payloads (objects or strings)
 *   - existingKeys: array of natural keys (names/titles) already present for the org
 *   - keyOf:        (rawItem) => natural key string for dedupe
 *   - createOne:    async (rawItem) => void  — calls the page's EXISTING create endpoint
 *
 * Returns { created, skipped, error } so the banner can report the true outcome.
 * Duplicate protection is case-insensitive + trimmed. A mid-batch failure never
 * rolls back earlier successes — the rest still get created and the error is
 * surfaced in the returned shape.
 */
export const natKey = (s) => String(s ?? "").trim().toLowerCase();

/**
 * Turn any thrown error (incl. FastAPI 422 whose `detail` is an ARRAY of objects,
 * which is what produced the "[object Object]" toast) into a readable string.
 */
export function extractErrorMessage(e, fallback = "Unknown error") {
  const d = e?.response?.data;
  let msg = d?.message ?? d?.detail ?? e?.message;
  if (Array.isArray(msg)) {
    msg = msg
      .map((x) => (x && typeof x === "object" ? (x.msg || JSON.stringify(x)) : String(x)))
      .join("; ");
  } else if (msg && typeof msg === "object") {
    msg = msg.msg || msg.detail || JSON.stringify(msg);
  }
  return msg || fallback;
}

export async function persistAcceptedSuggestions({ items, existingKeys, keyOf, createOne }) {
  const seen = new Set((existingKeys || []).map(natKey));
  let created = 0;
  let skipped = 0;
  let alreadyAccepted = 0;
  let error;
  for (const raw of items || []) {
    const key = natKey(keyOf(raw));
    if (!key) { skipped += 1; continue; }
    if (seen.has(key)) { skipped += 1; alreadyAccepted += 1; continue; }
    try {
      await createOne(raw);
      created += 1;
      seen.add(key);
    } catch (e) {
      error = extractErrorMessage(e, "Some items could not be added");
    }
  }
  return { created, skipped, alreadyAccepted, error };
}

/** Extract a display name/key from a suggestion item that may be an object or string. */
export function suggestionName(raw) {
  if (raw && typeof raw === "object") {
    return raw.name || raw.scenario_name || raw.role || raw.title || raw.threat ||
      raw.function_name || raw.action || Object.values(raw)[0] || "";
  }
  return String(raw ?? "");
}
